<template>
  <v-row class="user-bio-panel">
    <v-col cols="12">
      <v-card class="pt-10">
        <v-card-title
          v-touch="{
            left: () =>
              goToNext(candidateEntry.candidateEntryIds, candidateEntry.id, loading, {
                name: 'recruitment-candidates-entry-view',
                param: 'id',
              }),
            right: () =>
              goToPrevious(candidateEntry.candidateEntryIds, candidateEntry.id, loading, {
                name: 'recruitment-candidates-entry-view',
                param: 'id',
              }),
          }"
          class="justify-center flex-column"
        >
          <div class="d-flex justify-space-around align-center w-full">
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToPrevious(candidateEntry.candidateEntryIds, candidateEntry.id) || loading"
              @click="
                goToPrevious(candidateEntry.candidateEntryIds, candidateEntry.id, loading, {
                  name: 'recruitment-candidates-entry-view',
                  param: 'id',
                })
              "
              ><v-icon>{{ icons.mdiChevronLeft }}</v-icon></v-btn
            >
            <v-avatar
              :color="candidateEntry.avatar ? '' : 'primary'"
              :class="candidateEntry.avatar ? '' : 'v-avatar-light-bg primary--text'"
              size="120"
              rounded
              class="mb-4"
            >
              <v-img
                v-if="candidateEntry.avatar"
                :src="require(`@/assets/images/avatars/${candidateEntry.avatar}`)"
              />
              <span v-else class="font-weight-semibold text-5xl">{{
                avatarText(`${candidateEntry.firstName} ${candidateEntry.lastName}`)
              }}</span>
            </v-avatar>
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToNext(candidateEntry.candidateEntryIds, candidateEntry.id) || loading"
              @click="
                goToNext(candidateEntry.candidateEntryIds, candidateEntry.id, loading, {
                  name: 'recruitment-candidates-entry-view',
                  param: 'id',
                })
              "
            >
              <v-icon>{{ icons.mdiChevronRight }}</v-icon>
            </v-btn>
          </div>

          <span class="mb-2">{{ candidateEntry.firstName }} {{ candidateEntry.lastName }}</span>
          <v-chip
            label
            small
            :color="resolveGDPRStatusColor(candidateEntry.gdprStatus).color"
            :class="`v-chip-light-bg ${
              resolveGDPRStatusColor(candidateEntry.gdprStatus).color
            }--text font-weight-semibold text-capitalize ma-1 mb-2`"
            >{{ $t(resolveGDPRStatusColor(candidateEntry.gdprStatus).text) }}</v-chip
          >
          <span class="d-flex justify-center align-center flex-wrap">
            <v-chip
              v-for="(tag, index) in candidateEntry.tags"
              :key="`Talent-Pool-Entry--Tag-${index}`"
              label
              small
              color="info"
              outlined
              class="font-weight-semibold ma-1"
              v-text="tag.tag"
            />
          </span>
        </v-card-title>

        <v-card-text>
          <h2 v-if="isDeleteRequested" v-t="'anonymizationRequest'" class="text-xl font-weight-semibold mb-2" />
          <v-divider v-if="isDeleteRequested" />
          <v-list v-if="isDeleteRequested">
            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('anonymizationRequestRemaining') }}:</span>
              <span class="text--secondary"
                >{{ candidateEntry.consent.anonymizationRequestRemaining }} {{ $t('days') }}</span
              >
            </v-list-item>
          </v-list>
          <v-row v-if="isDeleteRequested" justify="center" class="my-2">
            <v-col cols="auto">
              <v-btn small color="error" @click="candidateDeclineAnonymizationRequestHandler">
                <v-icon class="me-2" size="16">{{ icons.mdiClose }}</v-icon>
                <span v-t="'declineRequest'" />
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn small color="success" @click="candidateAcceptAnonymizationRequestHandler">
                <v-icon class="me-2" size="16">{{ icons.mdiCheck }}</v-icon>
                <span v-t="'acceptRequest'" />
              </v-btn>
            </v-col>
          </v-row>

          <h2 v-t="'details'" class="text-xl font-weight-semibold mb-2" :class="isDeleteRequested ? ['mt-4'] : []" />
          <v-divider></v-divider>
          <v-list>
            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('firstName', { postfix: '' }) }}:</span>
              <span class="text--secondary">{{ candidateEntry.firstName }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('lastName', { postfix: '' }) }}:</span>
              <span class="text--secondary">{{ candidateEntry.lastName }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('gdprStatus') }}:</span>
              <div v-if="gdprConsentValidUntil" class="d-flex align-center flex-wrap">
                <v-chip
                  label
                  small
                  :color="isGdprConsentExpired ? 'error' : 'success'"
                  :class="`v-chip-light-bg ${
                    isGdprConsentExpired ? 'error' : 'success'
                  }--text font-weight-semibold text-capitalize mb-0`"
                >
                  {{ $t(isGdprConsentExpired ? 'expired' : 'valid') }}
                </v-chip>
                <span
                  class="text-caption ms-2"
                  :class="isGdprConsentExpired ? 'error--text' : 'text--secondary'"
                >
                  {{ gdprConsentValidUntilLabel }}
                </span>
              </div>
              <v-chip
                v-else
                label
                small
                :color="resolveGDPRStatusColor(candidateEntry.gdprStatus).color"
                :class="`v-chip-light-bg ${
                  resolveGDPRStatusColor(candidateEntry.gdprStatus).color
                }--text font-weight-semibold text-capitalize mb-0`"
              >
                {{ $t(resolveGDPRStatusColor(candidateEntry.gdprStatus).text) }}
              </v-chip>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('email', { postfix: '' }) }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(candidateEntry.email) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('phoneNumber') }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(candidateEntry.phone) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('invitedBy') }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(candidateEntry.invitedBy) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('createdAt') }}:</span>
              <span class="text--secondary">{{ formatDateToMonthShort(candidateEntry.createdAt) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('updatedAt') }}:</span>
              <span class="text--secondary">{{ formatDateToMonthShort(candidateEntry.updatedAt) }}</span>
            </v-list-item>

            <template v-if="pinnedDetailsFields.length">
              <v-divider class="my-2" />
              <v-list-item
                v-for="cf in pinnedDetailsFields"
                :key="cf.id"
                dense
                class="px-0 my-n2"
              >
                <span class="font-weight-medium text-no-wrap me-2">{{ cf.name }}:</span>
                <span class="text--secondary">{{ formatCustomFieldDisplayValue(cf, $t) }}</span>
              </v-list-item>
            </template>
          </v-list>

          <template v-if="pinnedCustomFieldsBySection.length">
            <template v-for="section in pinnedCustomFieldsBySection">
              <h3 :key="`section-heading-${section.name}`" class="text-lg font-weight-semibold mt-4 mb-2">
                {{ section.name }}
              </h3>
              <v-divider :key="`section-divider-${section.name}`" class="mb-2" />
              <v-list :key="`section-list-${section.name}`">
                <v-list-item
                  v-for="cf in section.fields"
                  :key="cf.id"
                  dense
                  class="px-0 my-n2"
                >
                  <span class="font-weight-medium text-no-wrap me-2">{{ cf.name }}:</span>
                  <span class="text--secondary">{{ formatCustomFieldDisplayValue(cf, $t) }}</span>
                </v-list-item>
              </v-list>
            </template>
          </template>

          <v-row justify="center" class="my-2">
            <v-col v-if="candidateEntry.canInviteToJob" cols="auto">
              <v-btn small color="primary" outlined @click="isInviteToJobDialogOpen = !isInviteToJobDialogOpen">
                <v-icon class="me-2" size="16">{{ icons.mdiBriefcaseUploadOutline }}</v-icon>
                <span v-t="'inviteToJob'" />
              </v-btn>
            </v-col>
          </v-row>
          <v-row justify="center" class="my-2">
            <v-col v-if="candidateEntry.canSetForAnonymizationStatus" cols="auto">
              <v-btn small color="info" outlined @click="candidateEntrySetForAnonymizationHandler">
                <span v-t="'setForAnonymization'" />
              </v-btn>
            </v-col>
            <v-col v-if="candidateEntry.canAutoAnonymizationDisabledStatus" cols="auto">
              <v-btn small color="warning" outlined @click="candidateEntryAutoAnonymizationDisabledHandler">
                <span v-t="'autoAnonymizationDisabled'" />
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-dialog
        v-if="candidateEntry.canInviteToJob"
        v-model="isInviteToJobDialogOpen"
        max-width="850px"
        persistent
        overlay-color="#7f7f7f"
        overlay-opacity="0.2"
      >
        <v-card class="pa-sm-10 pa-3">
          <v-card-title v-t="'inviteToJob'" class="justify-center text-h5" />
          <v-card-text>
            <v-row>
              <v-col cols="12" class="d-flex align-center justify-center flex-column">
                <div v-if="!selectedEmail" v-t="'confirmationModalDefaultEmail'" />
                <div v-else v-t="'confirmationModalDefaultEmailConditions'" />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="job"
                  :label="$t('selectJob')"
                  :placeholder="$t('selectJob')"
                  :items="jobChoices"
                  item-value="id"
                  item-text="title"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="selectedEmail"
                  :label="$t('selectEmail')"
                  :placeholder="$t('selectEmail')"
                  :items="emailTemplatesChoices"
                  outlined
                  dense
                  hide-details="auto"
                  @change="setEmailTemplateData"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="cc"
                  :label="$t('cc')"
                  :placeholder="$t('cc')"
                  outlined
                  dense
                  maxlength="100"
                  hide-details="auto"
                  :hint="$t('separateByComma')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="5">
                <v-switch v-model="sendCopyToRecruiter" class="mt-0" inset color="primary">
                  <template v-slot:label> {{ $t('sendMeCopy') }} </template>
                </v-switch>
              </v-col>
            </v-row>
            <v-form v-if="selectedEmail">
              <v-row>
                <v-col cols="12" sm="7">
                  <v-text-field
                    v-model="selectedEmailTemplateSubject"
                    :label="$t('subject')"
                    :placeholder="$t('subject')"
                    outlined
                    dense
                    maxlength="100"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="5">
                  <v-select
                    v-model="selectedEmailTemplateLang"
                    outlined
                    dense
                    :items="[
                      {
                        text: $t('pl'),
                        value: 'pl',
                      },
                      {
                        text: $t('eng'),
                        value: 'eng',
                      },
                    ]"
                    :label="$t('language')"
                    :placeholder="$t('language')"
                    hide-details="auto"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <summer-note
                    :config="{ height: 300 }"
                    hide-link-and-image-button
                    @ready="readySummerNote"
                  ></summer-note>
                </v-col>
              </v-row>
            </v-form>
            <v-row>
              <v-col cols="12" class="d-flex justify-center mt-3">
                <v-btn v-t="'discard'" outlined class="me-3" @click="closeInviteToJobDialog" />
                <v-btn color="primary" :loading="sendingInvitationToJob" :disabled="!job" @click="sendInvitation">
                  {{ $t('invite') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import {
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiBriefcaseOutline,
  mdiClose,
  mdiArrowRight,
  mdiCheck,
  mdiBriefcaseUploadOutline,
  mdiChevronLeft,
  mdiChevronRight,
} from '@mdi/js'
import { ref, computed, getCurrentInstance, nextTick } from '@vue/composition-api'
import { formatConsentValidUntilLabel, isConsentExpired } from '@/utils/gdprConsentUtils'
import { useState, useActions } from 'vuex-composition-helpers'

import { avatarText, formatDateToMonthShort, emptyValueFormatter } from '@core/utils/filter'
import {
  getPinnedCustomFieldsBySection,
  getPinnedDetailsFields,
  safeFormatDate,
  formatCustomFieldDisplayValue,
} from '@/utils/customFieldUtils'
import { invoke, until } from '@vueuse/core'
import SummerNote from '@/plugins/summer-note/SummerNote.vue'
import { EMAIL_VARIABLES_INVITE_TO_JOB_FROM_TALENT_POOL_SUMMERNOTE } from '@/plugins/summer-note/variables'
import useCandidatesList from '@/views/recruitment/candidates/candidates-list/useCandidatesList'
import navigationUtils from '@/utils/navigationUtils'

export default {
  components: {
    SummerNote,
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup(_, { emit }) {
    const vm = getCurrentInstance().proxy
    const translate = (key, params) => vm.$t(key, params)
    const { resolveGDPRStatusColor } = useCandidatesList()

    const isInviteToJobDialogOpen = ref(false)

    const { getEmailTemplatesChoices, getJobChoices } = useActions('jobs', [
      'getEmailTemplatesChoices',
      'getJobChoices',
    ])
    const {
      candidateEntryAutoAnonymizationDisabled,
      candidateEntrySetForAnonymization,
      candidateAcceptAnonymizationRequest,
      candidateDeclineAnonymizationRequest,
      sendInvitationToJob,
    } = useActions('candidates', [
      'candidateEntryAutoAnonymizationDisabled',
      'candidateEntrySetForAnonymization',
      'candidateAcceptAnonymizationRequest',
      'candidateDeclineAnonymizationRequest',
      'sendInvitationToJob',
    ])
    const { candidateEntry } = useState('candidates', ['candidateEntry'])

    const { canGoToNext, goToNext, canGoToPrevious, goToPrevious } = navigationUtils()

    const isDeleteRequested = computed(() => {
      return candidateEntry.value.gdprStatus === 'anonymization_request'
    })

    const gdprConsentValidUntil = computed(() => candidateEntry.value.consent?.validUntil || null)

    const isGdprConsentExpired = computed(() =>
      isConsentExpired(candidateEntry.value.gdprStatus, gdprConsentValidUntil.value),
    )

    const gdprConsentValidUntilLabel = computed(() =>
      formatConsentValidUntilLabel(gdprConsentValidUntil.value, translate),
    )

    const pinnedDetailsFields = computed(() => {
      return getPinnedDetailsFields(candidateEntry.value.customFields || [], {
        excludeScope: 'employee',
      })
    })

    const pinnedCustomFieldsBySection = computed(() => {
      return getPinnedCustomFieldsBySection(candidateEntry.value.customFields || [], {
        excludeScope: 'employee',
      })
    })

    const job = ref(null)
    const jobChoices = ref([])

    const emailTemplatesChoices = ref([])
    const selectedEmail = ref(null)
    const selectedEmailTemplateHtml = ref('')
    const selectedEmailTemplateSubject = ref('')
    const selectedEmailTemplateLang = ref('')
    const cc = ref('')
    const sendCopyToRecruiter = ref(false)

    const sendingInvitationToJob = ref(false)

    const editor = ref(null)
    const emailVariablesSummernote = ref([])
    emailVariablesSummernote.value = EMAIL_VARIABLES_INVITE_TO_JOB_FROM_TALENT_POOL_SUMMERNOTE(vm)
    const readySummerNote = (initializedEditor) => {
      editor.value = initializedEditor
      editor.value.summernote('code', selectedEmailTemplateHtml.value)
      editor.value.$on('change', (content) => {
        selectedEmailTemplateHtml.value = content
      })
    }

    const setEmailTemplateData = () => {
      const emailTemplate = emailTemplatesChoices.value.find((et) => et.value === selectedEmail.value)
      selectedEmailTemplateHtml.value = emailTemplate?.bodyHtml
      selectedEmailTemplateSubject.value = emailTemplate?.subject
      selectedEmailTemplateLang.value = emailTemplate?.lang
      editor.value?.summernote('code', selectedEmailTemplateHtml.value)
    }

    const resetEmailTemplateData = () => {
      selectedEmail.value = null
      selectedEmailTemplateHtml.value = ''
      selectedEmailTemplateSubject.value = ''
      selectedEmailTemplateLang.value = ''
      cc.value = ''
      sendCopyToRecruiter.value = false
      editor.value?.summernote('code', selectedEmailTemplateHtml.value)
    }

    const closeInviteToJobDialog = () => {
      isInviteToJobDialogOpen.value = false
      job.value = null
      sendingInvitationToJob.value = false
      resetEmailTemplateData()
    }

    invoke(async () => {
      await until(computed(() => isInviteToJobDialogOpen.value)).toBe(true)
      nextTick(async () => {
        jobChoices.value = await getJobChoices({
          candidate: candidateEntry.value.candidateId,
          stage: 'ongoing',
        })
        emailTemplatesChoices.value = await getEmailTemplatesChoices({ actionType: 'invite_to_job_tp' })
      })
    })

    const candidateEntrySetForAnonymizationHandler = async () => {
      await candidateEntrySetForAnonymization(candidateEntry.value.id)
      emit('refetchCandidateEntryDetails')
    }

    const candidateEntryAutoAnonymizationDisabledHandler = async () => {
      await candidateEntryAutoAnonymizationDisabled(candidateEntry.value.id)
      emit('refetchCandidateEntryDetails')
    }

    const candidateAcceptAnonymizationRequestHandler = async () => {
      await candidateAcceptAnonymizationRequest(candidateEntry.value.id)
      emit('refetchCandidateEntryDetails')
    }

    const candidateDeclineAnonymizationRequestHandler = async () => {
      await candidateDeclineAnonymizationRequest(candidateEntry.value.id)
      emit('refetchCandidateEntryDetails')
    }

    const sendInvitation = async () => {
      sendingInvitationToJob.value = true
      await sendInvitationToJob({
        candidateId: candidateEntry.value.id,
        payload: {
          inviteJob: job.value,
          bodyHtml: selectedEmailTemplateHtml.value,
          subject: selectedEmailTemplateSubject.value,
          lang: selectedEmailTemplateLang.value,
          cc: cc.value,
          sendCopyToRecruiter: sendCopyToRecruiter.value,
        },
      })
      closeInviteToJobDialog()
    }

    return {
      emit,

      formatDateToMonthShort,
      emptyValueFormatter,
      safeFormatDate,
      formatCustomFieldDisplayValue,
      pinnedDetailsFields,
      pinnedCustomFieldsBySection,
      gdprConsentValidUntil,
      isGdprConsentExpired,
      gdprConsentValidUntilLabel,
      resolveGDPRStatusColor,
      avatarText,
      candidateEntrySetForAnonymizationHandler,
      candidateEntryAutoAnonymizationDisabledHandler,
      candidateAcceptAnonymizationRequestHandler,
      candidateDeclineAnonymizationRequestHandler,
      canGoToNext,
      goToNext,
      canGoToPrevious,
      goToPrevious,

      isDeleteRequested,

      job,
      jobChoices,
      candidateEntry,
      isInviteToJobDialogOpen,
      sendingInvitationToJob,

      emailTemplatesChoices,
      selectedEmail,
      selectedEmailTemplateHtml,
      selectedEmailTemplateSubject,
      selectedEmailTemplateLang,
      cc,
      sendCopyToRecruiter,

      setEmailTemplateData,
      closeInviteToJobDialog,
      sendInvitation,

      editor,
      emailVariablesSummernote,
      readySummerNote,

      icons: {
        mdiCheckCircleOutline,
        mdiCloseCircleOutline,
        mdiBriefcaseOutline,
        mdiClose,
        mdiArrowRight,
        mdiCheck,
        mdiBriefcaseUploadOutline,
        mdiChevronLeft,
        mdiChevronRight,
      },
    }
  },
}
</script>
